import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Building2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.pexels.com/photos/18153132/pexels-photo-18153132.jpeg";

export default function Register() {
  const { login, setUser } = useAuth();
  const nav = useNavigate();
  const [config, setConfig] = useState({ countries: [], languages: [] });
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "",
    country: "", region: "", city: "", currency: "", language: "en" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState("");
  const [devOtp, setDevOtp] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  const selectedCountry = config.countries.find((c) => c.name === form.country);

  const onCountry = (name) => {
    const c = config.countries.find((x) => x.name === name);
    setForm({ ...form, country: name, region: "", currency: c?.currency || "",
      language: c?.languages?.[0] || form.language });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, role: "customer" });
      login(data);
      setDevOtp(data.dev_otp);
      if (data.dev_otp) setOtp(data.dev_otp);
      setOtpStep(true);
      toast.success("Account created — verify to continue");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    try {
      await api.post("/auth/verify-otp", { email: form.email, code: otp });
      setUser((u) => (u ? { ...u, verified: true } : u));
      toast.success("Verified!");
      nav("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">
              <Building2 className="h-5 w-5" aria-hidden="true" />
            </div>
            <span className="font-heading text-xl font-bold">Nustamac / Walo</span>
          </div>
          <div>
            <p className="overline text-white/70 mb-3">Register your property</p>
            <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-tight max-w-md">
              Build. Transform. Care.
            </h1>
            <p className="mt-4 text-white/80 max-w-md">
              One platform for construction, renovation and lifelong property maintenance — worldwide.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm fade-up">
          {!otpStep ? (
            <>
              <p className="overline text-accent mb-2">Get started</p>
              <h2 className="font-heading text-3xl font-bold tracking-tight mb-6">Create account</h2>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" required value={form.name} onChange={set("name")} data-testid="register-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" required value={form.email} onChange={set("email")} data-testid="register-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} data-testid="register-password-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Select value={form.country} onValueChange={onCountry}>
                      <SelectTrigger data-testid="register-country-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {config.countries.map((c) => <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Region</Label>
                    <Select value={form.region} onValueChange={(v) => setForm({ ...form, region: v })} disabled={!selectedCountry}>
                      <SelectTrigger data-testid="register-region-select"><SelectValue placeholder="Region" /></SelectTrigger>
                      <SelectContent>
                        {(selectedCountry?.regions || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input id="city" value={form.city} onChange={set("city")} data-testid="register-city-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" value={form.phone} onChange={set("phone")} placeholder="+237…" data-testid="register-phone-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Currency</Label>
                    <Input value={form.currency} readOnly placeholder="—" data-testid="register-currency-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Language</Label>
                    <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                      <SelectTrigger data-testid="register-language-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {config.languages.map((l) => <SelectItem key={l.code} value={l.code}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
                <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full rounded-full hover:bg-primary/90">
                  {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create account
                </Button>
              </form>
              <p className="text-sm text-muted-foreground mt-6">
                Already registered?{" "}
                <Link to="/login" className="text-primary font-medium hover:underline" data-testid="go-login-link">Sign in</Link>
              </p>
            </>
          ) : (
            <div data-testid="otp-step">
              <div className="h-12 w-12 rounded-full bg-secondary flex items-center justify-center mb-4">
                <ShieldCheck className="h-6 w-6 text-primary" aria-hidden="true" />
              </div>
              <p className="overline text-accent mb-2">Verify account</p>
              <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">Enter code</h2>
              <p className="text-sm text-muted-foreground mb-6">
                We sent a 6-digit verification code to {form.phone || form.email}.
              </p>
              {devOtp && (
                <div className="mb-4 rounded-lg border border-border bg-secondary/50 p-3 text-xs text-muted-foreground" data-testid="dev-otp-hint">
                  Demo code (SMS not configured): <span className="font-mono font-semibold text-foreground">{devOtp}</span>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="otp">Verification code</Label>
                <Input id="otp" value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6}
                  className="tracking-[0.5em] text-center text-lg font-mono" data-testid="otp-input" />
              </div>
              {error && <p className="text-sm text-destructive mt-3" data-testid="otp-error">{error}</p>}
              <Button onClick={verify} disabled={loading || otp.length < 6} className="w-full rounded-full mt-5 hover:bg-primary/90" data-testid="verify-otp-button">
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Verify & Continue
              </Button>
              <button onClick={() => nav("/dashboard")} className="w-full text-sm text-muted-foreground mt-4 hover:text-foreground" data-testid="skip-otp-button">
                Skip for now
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
